import { useEffect, useState } from "react";
import { Copy, Check, Gift, Share2, ChevronLeft, MessageCircle, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CUR_AR = { SAR: "ر.س", AED: "د.إ", KWD: "د.ك", QAR: "ر.ق", BHD: "د.ب", OMR: "ر.ع" };
const fmtDate = (iso, en) => (iso ? new Date(iso).toLocaleDateString(en ? "en-GB" : "ar-SA-u-ca-gregory", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function ReferralCard() {
  const { lang } = useI18n();
  const en = lang === "en";
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data?.eligible) return null;
  const cur = en ? data.currency : (CUR_AR[data.currency] || data.currency);
  const money = (v) => `${v} ${cur}`;
  const shareText = en
    ? `Get 10% off Calofay with my link: ${data.link}`
    : `اشترك في كالوفاي بخصم 10% من خلال رابطي: ${data.link}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.link); } catch { /* older webviews */ }
    setCopied(true);
    toast.success(en ? "Link copied" : "تم نسخ الرابط");
    setTimeout(() => setCopied(false), 1800);
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); return; } catch { return; }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  return (
    <>
      <div className="w-full rounded-3xl p-5 bg-neutral-950 text-white relative overflow-hidden" data-testid="referral-card">
        <div className="pointer-events-none absolute -top-14 -end-14 w-44 h-44 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-300 to-brand-500 flex items-center justify-center shrink-0">
            <Gift className="w-6 h-6 text-neutral-950" />
          </div>
          <div className="flex-1">
            <div className="font-black text-lg" data-testid="referral-title">{en ? "Share & Earn" : "شارك واربح"}</div>
            <div className="text-xs text-neutral-300 leading-relaxed" data-testid="referral-subtitle">
              {en
                ? <>Earn <b className="text-amber-300">{money(data.commission)}</b> for every person who subscribes through you — and they get 10% off.</>
                : <>على كل عميل يشترك من طرفك ستحصل على <b className="text-amber-300">{money(data.commission)}</b> — وصديقك يحصل على خصم 10%.</>}
            </div>
          </div>
        </div>

        <div className="relative flex items-center gap-2 bg-white/10 border border-white/15 rounded-2xl p-1.5 ps-3">
          <span className="flex-1 truncate text-sm font-semibold num" dir="ltr" data-testid="referral-link">{data.link.replace(/^https?:\/\//, "")}</span>
          <button onClick={copy} className={`shrink-0 text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1 transition-colors ${copied ? "bg-emerald-500 text-white" : "bg-white text-neutral-900"}`} data-testid="referral-copy-btn">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {en ? "Copy" : "نسخ"}
          </button>
        </div>

        <div className="relative mt-3 grid grid-cols-2 gap-2">
          <button onClick={share} className="py-2.5 rounded-xl bg-gradient-to-l from-amber-300 to-brand-500 text-neutral-950 text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform" data-testid="referral-share-btn">
            <Share2 className="w-4 h-4" /> {en ? "Share link" : "شارك الرابط"}
          </button>
          <button onClick={() => setOpen(true)} className="py-2.5 rounded-xl bg-white/10 border border-white/15 text-sm font-bold flex items-center justify-center gap-1 active:scale-[0.98] transition-transform" data-testid="referral-details-btn">
            {en ? "Tap for details" : "اضغط للتفاصيل"} <ChevronLeft className={`w-4 h-4 ${en ? "-scale-x-100" : ""}`} />
          </button>
        </div>

        <div className="relative mt-3 flex items-center justify-between text-xs text-neutral-300">
          <span data-testid="referral-count">{en ? "Subscribers via you" : "اشتركوا من طرفك"}: <b className="text-white num">{data.count_active}</b></span>
          <span data-testid="referral-balance">{en ? "Balance" : "رصيدك"}: <b className="text-emerald-300 num">{money(data.balance)}</b></span>
        </div>
      </div>

      <ReferralDetails open={open} onOpenChange={setOpen} data={data} en={en} money={money} />
    </>
  );
}

const STATUS = {
  pending:   { ar: "قيد الانتظار", en: "Pending",   cls: "bg-amber-50 text-amber-700",     Icon: Clock },
  confirmed: { ar: "مؤكدة",        en: "Confirmed", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  cancelled: { ar: "ملغاة (استرجاع)", en: "Cancelled (refund)", cls: "bg-red-50 text-red-600", Icon: XCircle },
};

function Stat({ label, value, tone, testid }) {
  return (
    <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-3" data-testid={testid}>
      <div className="text-[11px] text-neutral-500">{label}</div>
      <div className={`num font-black text-base ${tone || "text-neutral-900"}`}>{value}</div>
    </div>
  );
}

function ReferralDetails({ open, onOpenChange, data, en, money }) {
  const openWA = () => {
    const num = (data.whatsapp_number || "").replace(/\D/g, "");
    const msg = en
      ? `Hi, I'd like to withdraw my referral earnings.\nCode: ${data.code}\nBalance: ${money(data.balance)}`
      : `مرحباً، أرغب بصرف عمولة شارك واربح.\nالكود: ${data.code}\nالرصيد: ${money(data.balance)}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const rules = en ? [
    `You earn ${money(data.commission)} on the first payment of every new subscriber who uses your link or code ${data.code}.`,
    "Each commission stays “pending” for 15 days (our money-back guarantee period), then is confirmed automatically. It's cancelled if the subscriber gets a refund.",
    `Minimum payout is ${money(data.min_payout)} (2 subscribers).`,
    "Payouts are made monthly, on one day each month.",
    "Once your balance reaches the minimum, message us on WhatsApp to receive it.",
    "The 10% discount applies when subscribing on the website. You can't use your own code.",
  ] : [
    `تحصل على ${money(data.commission)} عن أول دفعة لكل مشترك جديد يستخدم رابطك أو كودك ${data.code}.`,
    "تبقى كل عمولة «قيد الانتظار» لمدة 15 يوم (فترة ضمان الاسترجاع) ثم تتأكد تلقائياً، وتُلغى إذا استرجع المشترك المبلغ.",
    `الحد الأدنى للصرف ${money(data.min_payout)} (مشتركين اثنين على الأقل).`,
    "يتم الصرف بشكل شهري في يوم واحد من كل شهر.",
    "عند وصول رصيدك للحد الأدنى تواصل معنا على واتساب ليتم صرف المبلغ لك.",
    "خصم 10% لصاحبك عند الاشتراك، ولا يمكن استخدام كودك لنفسك.",
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto rounded-3xl" dir={en ? "ltr" : "rtl"} data-testid="referral-details-modal">
        <DialogHeader>
          <DialogTitle className="text-start pr-8">{en ? "Share & Earn — details" : "شارك واربح — التفاصيل"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <Stat label={en ? "Subscribers via you" : "اشتركوا من طرفك"} value={data.count_active} testid="ref-stat-count" />
          <Stat label={en ? "Available balance" : "الرصيد المتاح للصرف"} value={money(data.balance)} tone="text-emerald-600" testid="ref-stat-balance" />
          <Stat label={en ? "Pending" : "قيد الانتظار"} value={money(data.pending)} tone="text-amber-600" testid="ref-stat-pending" />
          <Stat label={en ? "Paid to you" : "تم صرفه لك"} value={money(data.paid)} testid="ref-stat-paid" />
        </div>

        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-neutral-800 space-y-2" data-testid="referral-rules">
          {rules.map((r) => (
            <div key={r} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>{r}</span></div>
          ))}
        </div>

        <button onClick={openWA} className={`w-full py-3 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 ${data.can_withdraw ? "bg-[#25D366]" : "bg-[#25D366]/70"}`} data-testid="referral-withdraw-wa">
          <MessageCircle className="w-4 h-4" />
          {data.can_withdraw ? (en ? "Request payout on WhatsApp" : "اطلب صرف المبلغ عبر واتساب") : (en ? "Contact us on WhatsApp" : "تواصل معنا عبر واتساب")}
        </button>

        <div>
          <div className="font-bold text-sm mb-2">{en ? "Your referrals" : "المشتركين من طرفك"}</div>
          {data.referrals.length === 0 ? (
            <div className="text-xs text-neutral-500 bg-neutral-50 rounded-2xl p-4 text-center" data-testid="referral-empty">
              {en ? "No subscribers yet — share your link to start earning." : "لا يوجد مشتركين بعد — شارك رابطك وابدأ تربح."}
            </div>
          ) : (
            <div className="space-y-2" data-testid="referral-list">
              {data.referrals.map((r, i) => {
                const s = STATUS[r.status] || STATUS.pending;
                return (
                  <div key={i} className="flex items-center gap-3 rounded-2xl border border-neutral-100 p-3" data-testid={`referral-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{r.name}</div>
                      <div className="text-[11px] text-neutral-500">
                        {fmtDate(r.date, en)}
                        {r.status === "pending" && <> · {en ? "confirms" : "تتأكد"} {fmtDate(r.confirm_date, en)}</>}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className={`num text-sm font-bold ${r.status === "cancelled" ? "line-through text-neutral-400" : ""}`}>{money(r.commission)}</div>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}><s.Icon className="w-3 h-3" />{en ? s.en : s.ar}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {data.payouts.length > 0 && (
          <div>
            <div className="font-bold text-sm mb-2">{en ? "Payouts" : "المبالغ المصروفة"}</div>
            <div className="space-y-1.5" data-testid="referral-payouts">
              {data.payouts.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs rounded-xl bg-neutral-50 px-3 py-2">
                  <span>{fmtDate(p.date, en)}{p.note ? ` · ${p.note}` : ""}</span>
                  <span className="num font-bold text-emerald-700">{money(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
